'use client';

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
} from 'react';

import { useSearchParams } from 'next/navigation';

import { IS_PRODUCTION } from '@/common/constants';

const DEBUG_MODE_KEY = 'bazak-debug-mode';
const DEBUG_URL_PARAM = 'debug';

interface DebugModeContextType {
  isDebugMode: boolean;
  toggleDebugMode: () => void;
  showToggle: boolean;
}

const DebugModeContext = createContext<DebugModeContextType | undefined>(undefined);

interface DebugModeProviderProps {
  children: ReactNode;
  /** When provided, controls debug mode directly — no localStorage or URL logic. */
  initialDebugMode?: boolean;
}

// ── localStorage helpers (only used when initialDebugMode is NOT provided) ──

function getLocalStorageDebugMode(): boolean {
  try {
    const stored = localStorage.getItem(DEBUG_MODE_KEY);
    return stored === null ? false : stored === 'true';
  } catch {
    return false;
  }
}

function setLocalStorageDebugMode(value: boolean): void {
  try {
    localStorage.setItem(DEBUG_MODE_KEY, String(value));
    window.dispatchEvent(new StorageEvent('storage', { key: DEBUG_MODE_KEY }));
  } catch {
    // Ignore localStorage errors
  }
}

function subscribeToDebugMode(callback: () => void): () => void {
  const handleStorageChange = (e: StorageEvent) => {
    if (e.key === DEBUG_MODE_KEY) {
      callback();
    }
  };
  window.addEventListener('storage', handleStorageChange);
  return () => window.removeEventListener('storage', handleStorageChange);
}

/**
 * Inner provider for development mode — has localStorage + URL param logic.
 * Only rendered when `initialDebugMode` is NOT provided.
 *
 * Uses `useSyncExternalStore` for localStorage to get cross-tab sync and
 * correct hydration (the server snapshot returns `false`, matching SSR output).
 * The DevToolsButton handles the visual mismatch via CSS data attributes.
 */
function DevDebugModeProvider({ children }: { children: ReactNode }) {
  const debugMode = useSyncExternalStore(
    subscribeToDebugMode,
    getLocalStorageDebugMode,
    () => false,
  );

  const debugFromUrl = useSearchParams().has(DEBUG_URL_PARAM);

  // Production stays URL-only: a stale localStorage flag must not follow a guest into a
  // real session. Locally both work, so `?debug=true` is not silently ignored on the one
  // build where someone is most likely to reach for it.
  const isDebugMode = IS_PRODUCTION ? debugFromUrl : debugMode || debugFromUrl;

  const toggleDebugMode = useCallback(() => {
    setLocalStorageDebugMode(!debugMode);
  }, [debugMode]);

  const value = useMemo(
    () => ({ isDebugMode, toggleDebugMode, showToggle: !IS_PRODUCTION }),
    [isDebugMode, toggleDebugMode],
  );

  return <DebugModeContext.Provider value={value}>{children}</DebugModeContext.Provider>;
}

function ControlledDebugModeProvider({
  children,
  debugMode,
}: {
  children: ReactNode;
  debugMode: boolean;
}) {
  const value = useMemo(
    () => ({ isDebugMode: debugMode, toggleDebugMode: () => {}, showToggle: false }),
    [debugMode],
  );
  return <DebugModeContext.Provider value={value}>{children}</DebugModeContext.Provider>;
}

export function DebugModeProvider({ children, initialDebugMode }: DebugModeProviderProps) {
  if (initialDebugMode !== undefined) {
    return (
      <ControlledDebugModeProvider debugMode={initialDebugMode}>
        {children}
      </ControlledDebugModeProvider>
    );
  }

  return <DevDebugModeProvider>{children}</DevDebugModeProvider>;
}

export function useDebugMode(): DebugModeContextType {
  const context = useContext(DebugModeContext);
  if (!context) {
    return { isDebugMode: false, toggleDebugMode: () => {}, showToggle: false };
  }
  return context;
}
